
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface ProfileModalProps {
  customerEmail: string;
  onClose: () => void;
}

interface ProfileData {
  shipping_address: string;
  shipping_city: string;
  shipping_district: string;
  shipping_postal_code: string;
  shipping_phone: string;
  billing_same_as_shipping: boolean;
  billing_name: string;
  billing_address: string;
  billing_city: string;
  billing_district: string;
  billing_postal_code: string;
  billing_tax_office: string;
  billing_tax_number: string;
}

export default function ProfileModal({ customerEmail, onClose }: ProfileModalProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'shipping' | 'billing'>('shipping');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [profile, setProfile] = useState<ProfileData>({
    shipping_address: '',
    shipping_city: '',
    shipping_district: '',
    shipping_postal_code: '',
    shipping_phone: '',
    billing_same_as_shipping: true,
    billing_name: '',
    billing_address: '',
    billing_city: '',
    billing_district: '',
    billing_postal_code: '',
    billing_tax_office: '',
    billing_tax_number: '',
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('customer_profiles')
        .select('*')
        .eq('customer_email', customerEmail)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        setProfile({
          shipping_address: data.shipping_address || '',
          shipping_city: data.shipping_city || '',
          shipping_district: data.shipping_district || '',
          shipping_postal_code: data.shipping_postal_code || '',
          shipping_phone: data.shipping_phone || '',
          billing_same_as_shipping: data.billing_same_as_shipping ?? true,
          billing_name: data.billing_name || '',
          billing_address: data.billing_address || '',
          billing_city: data.billing_city || '',
          billing_district: data.billing_district || '',
          billing_postal_code: data.billing_postal_code || '',
          billing_tax_office: data.billing_tax_office || '',
          billing_tax_number: data.billing_tax_number || '',
        });
      }
    } catch (error) {
      console.error('Profil yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');
    
    try {
      const { data: existing } = await supabase
        .from('customer_profiles')
        .select('id')
        .eq('customer_email', customerEmail)
        .maybeSingle();

      const profileData = {
        customer_email: customerEmail,
        ...profile,
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        const { error } = await supabase
          .from('customer_profiles')
          .update(profileData)
          .eq('customer_email', customerEmail);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('customer_profiles')
          .insert(profileData);
        if (error) throw error;
      }

      setSuccessMessage('Bilgileriniz başarıyla kaydedildi!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Profil kaydedilirken hata:', error);
      alert('Kaydetme sırasında bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof ProfileData, value: string | boolean) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3366FF]/10 rounded-xl flex items-center justify-center">
              <i className="ri-user-settings-line text-xl text-[#3366FF]"></i>
            </div>
            <div>
              <h2 className="text-gray-900 font-semibold text-lg">Profil Bilgilerim</h2>
              <p className="text-gray-500 text-sm">Kargo ve fatura adres bilgileriniz</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-4">
          <div className="flex gap-2 p-1 bg-gray-100 rounded-full">
            <button
              onClick={() => setActiveTab('shipping')}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'shipping'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-truck-line mr-2"></i>
              Kargo Adresi
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`flex-1 py-2.5 px-4 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'billing'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-file-text-line mr-2"></i>
              Fatura Bilgileri
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto max-h-[calc(90vh-220px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line text-3xl text-[#3366FF] animate-spin"></i>
            </div>
          ) : (
            <>
              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700 text-sm">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  {successMessage}
                </div>
              )}

              {activeTab === 'shipping' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-blue-700 text-sm flex items-center gap-2">
                      <i className="ri-information-line"></i>
                      Bu bilgiler zorunlu değildir. Siparişleriniz için kargo adresi bilgilerinizi buraya ekleyebilirsiniz.
                    </p>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1.5">Telefon Numarası</label>
                    <input
                      type="tel"
                      value={profile.shipping_phone}
                      onChange={(e) => handleChange('shipping_phone', e.target.value)}
                      placeholder="05XX XXX XX XX"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1.5">Adres</label>
                    <textarea
                      value={profile.shipping_address}
                      onChange={(e) => handleChange('shipping_address', e.target.value)}
                      placeholder="Mahalle, sokak, bina no, daire no..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1.5">İl</label>
                      <input
                        type="text"
                        value={profile.shipping_city}
                        onChange={(e) => handleChange('shipping_city', e.target.value)}
                        placeholder="İstanbul"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1.5">İlçe</label>
                      <input
                        type="text"
                        value={profile.shipping_district}
                        onChange={(e) => handleChange('shipping_district', e.target.value)}
                        placeholder="Kadıköy"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1.5">Posta Kodu</label>
                    <input
                      type="text"
                      value={profile.shipping_postal_code}
                      onChange={(e) => handleChange('shipping_postal_code', e.target.value)}
                      placeholder="34000"
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                    />
                  </div>
                </div>
              )}

              {activeTab === 'billing' && (
                <div className="space-y-4">
                  {/* Fatura adresi seçeneği */}
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={profile.billing_same_as_shipping}
                        onChange={(e) => handleChange('billing_same_as_shipping', e.target.checked)}
                        className="w-5 h-5 rounded border-gray-300 text-[#3366FF] focus:ring-[#3366FF] cursor-pointer"
                      />
                      <div>
                        <span className="text-gray-900 font-medium text-sm">Fatura adresim kargo adresiyle aynı</span>
                        <p className="text-gray-500 text-xs mt-0.5">Bu seçenek işaretliyse kargo adresiniz fatura adresi olarak kullanılır</p>
                      </div>
                    </label>
                  </div>

                  {!profile.billing_same_as_shipping && (
                    <>
                      <div className="pt-2">
                        <label className="block text-gray-700 text-sm font-medium mb-1.5">Fatura Adı / Ünvanı</label>
                        <input
                          type="text"
                          value={profile.billing_name}
                          onChange={(e) => handleChange('billing_name', e.target.value)}
                          placeholder="Ad Soyad veya Şirket Ünvanı"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1.5">Fatura Adresi</label>
                        <textarea
                          value={profile.billing_address}
                          onChange={(e) => handleChange('billing_address', e.target.value)}
                          placeholder="Mahalle, sokak, bina no, daire no..."
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none resize-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1.5">İl</label>
                          <input
                            type="text"
                            value={profile.billing_city}
                            onChange={(e) => handleChange('billing_city', e.target.value)}
                            placeholder="İstanbul"
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1.5">İlçe</label>
                          <input
                            type="text"
                            value={profile.billing_district}
                            onChange={(e) => handleChange('billing_district', e.target.value)}
                            placeholder="Kadıköy"
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-medium mb-1.5">Posta Kodu</label>
                        <input
                          type="text"
                          value={profile.billing_postal_code}
                          onChange={(e) => handleChange('billing_postal_code', e.target.value)}
                          placeholder="34000"
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1.5">Vergi Dairesi</label>
                          <input
                            type="text"
                            value={profile.billing_tax_office}
                            onChange={(e) => handleChange('billing_tax_office', e.target.value)}
                            placeholder="Kadıköy V.D."
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1.5">Vergi No / TC Kimlik No</label>
                          <input
                            type="text"
                            value={profile.billing_tax_number}
                            onChange={(e) => handleChange('billing_tax_number', e.target.value)}
                            placeholder="1234567890"
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#3366FF] focus:border-transparent outline-none"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer text-sm font-medium whitespace-nowrap"
          >
            Kapat
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2.5 bg-[#3366FF] hover:bg-[#2855DD] text-white rounded-lg transition-colors cursor-pointer text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Kaydediliyor...
              </>
            ) : (
              <>
                <i className="ri-save-line"></i>
                Kaydet
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
